import {
  BranchQuoteSupport,
  BranchQuoteSupportItemType,
  SupportQuoteItemLinkDetail,
  ViewConversationEntry
} from '../../api/dealer-api-interface-franchisee';
import { getCurrentUser } from '../../../webmodule-common/other/api/current-user';
import { getSupportTypeDisplayValue } from './conversation-helper';
import { LangBranchQuoteSupportNew } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-support-new-lang';
// Legacy emitSupportEvent removed: session no longer dispatches DOM events (service publish handles state updates)

/**
 * Per-ticket ephemeral UI/session state for Support tickets.
 * Holds conversation entries, editing entry id, distinct users, and loaded flag.
 * Does NOT perform API calls itself – the owning service injects data.
 */
export class SupportTicketSession {
  readonly id: string;
  /** Session kind: 'draft' for a newly created (not yet persisted) ticket, 'editing' for an existing persisted ticket */
  kind: 'draft' | 'editing' = 'editing';
  /** Support type cached for quick adoption/lookups (mirrors ticket.type once loaded). Null until known. */
  supportType: BranchQuoteSupportItemType | null = null;
  masterDocumentConversationId: string | null = null;
  entries: ViewConversationEntry[] = [];
  users: string[] = [];
  loaded = false;
  ticket: BranchQuoteSupport | null = null;
  recordVersion: string | null = null;
  supplierName: string = '';
  /** Immutable snapshot of metadata when editing began or after last successful save */
  baselineMeta: {
    subject: string | null;
    quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
    version: string | null; // recordVersion at snapshot time
    masterDocumentFilename: string | null;
  } | null = null;
  /** Draft (mutable) metadata edits */
  draftMeta: {
    subject: string | null;
    quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
    touched: { subject?: boolean; links?: boolean; masterDoc?: boolean };
    conflict: { subject?: boolean; links?: boolean; masterDoc?: boolean };
    masterDocumentFilename: string | null;
  } | null = null;

  /** Working draft conversation entry (new or existing persisted draft). */
  draftEntry: {
    id: string; // working id (existing entry id or new guid if not yet persisted)
    text: string;
    attachments: { filename: string; size?: number; contentType?: string; title?: string }[];
    users: string[];
    existingPersisted: boolean; // true if this maps to an entry inside entries[]
    draftFlag: boolean; // server draft flag (should remain true until explicitly posted)
    touched: { text?: boolean; attachments?: boolean; users?: boolean };
  } | null = null;

  /**
   * Baseline snapshot of the latest persisted draft conversation entry at the time it was (re)wrapped.
   * Used to distinguish "has a persisted draft" (old behavior) from "has unsaved changes to that persisted draft" (new behavior).
   * Cleared when the draft is finalized or discarded. Only updated when wrapping a persisted draft entry.
   */
  baselineDraftEntry: {
    id: string;
    text: string;
    attachments: string[]; // filenames only
    users: string[];
  } | null = null;

  /** Pending new conversation entries to be persisted on next unified save (saveAllChanges). */
  pendingNewEntries: {
    id: string;
    text: string;
    users: string[];
    draft: boolean;
  }[] = [];

  constructor(id: string, kind: 'draft' | 'editing' = 'editing', supportType?: BranchQuoteSupportItemType) {
    this.id = id;
    this.kind = kind;
    this.supportType = typeof supportType === 'number' ? supportType : null;
  }

  /**
   * Ensure or (re)stage the working draft entry following unified rules.
   * Extended rule: Only treat a persisted draft as eligible if authored by the current user.
   * Rules:
   * 1. Locate persisted draft (entries[].draft === true AND authored by current user). If found and draftEntry.id matches AND !forceUpdate -> no-op.
   *    If forceUpdate OR id mismatch OR no draftEntry -> wrap persisted draft.
   * 2. If no eligible persisted draft:
   *    a. If no draftEntry -> create new unsaved draft.
   *    b. If draftEntry exists and its id collides with ANY saved entry id -> replace with new unsaved draft.
   *    c. Otherwise leave existing draftEntry untouched.
   * 3. When wrapping persisted draft, touched flags are reset and baseline snapshot captured.
   */
  ensureDraftEntry(newIdFactory: () => string, forceUpdate?: boolean): void {
    const currentUserId = getCurrentUser()?.id || null;
    // Eligible persisted draft must belong to current user (systemUserId match OR users array contains id)
    const persistedDraft =
      this.entries.find(e => e.draft === true && !!currentUserId && e.systemUserId === currentUserId) || null;
    if (persistedDraft) {
      const matchesCurrent = !!this.draftEntry && this.draftEntry.id === persistedDraft.id;
      if (matchesCurrent && !forceUpdate) return; // rule 1: do nothing
      // (Re)wrap persisted draft (either forceUpdate or different id / no current draftEntry)
      this.draftEntry = {
        id: persistedDraft.id,
        text: persistedDraft.text || '',
        attachments: (persistedDraft.attachments || []).map(a => ({ filename: a.filename })),
        users: persistedDraft.users || [],
        existingPersisted: true,
        draftFlag: true,
        touched: {}
      };
      // Capture (or refresh) baseline snapshot for persisted draft.
      this.baselineDraftEntry = {
        id: persistedDraft.id,
        text: persistedDraft.text || '',
        attachments: (persistedDraft.attachments || []).map(a => a.filename),
        users: persistedDraft.users || []
      };
      return;
    }
    // No eligible persisted draft present (either none or belongs to a different user)
    if (!this.draftEntry) {
      // Create brand new unsaved draft (rule 2a)
      const newId = newIdFactory();
      this.draftEntry = {
        id: newId,
        text: '',
        attachments: [],
        users: [],
        existingPersisted: false,
        draftFlag: true,
        touched: {}
      };
      return;
    }
    // Existing draftEntry but ensure it does not collide with ANY saved entry id (rule 2b)
    const collision = this.entries.some(e => e.id === this.draftEntry!.id);
    if (collision) {
      const newId = newIdFactory();
      this.draftEntry = {
        id: newId,
        text: '',
        attachments: [],
        users: [],
        existingPersisted: false,
        draftFlag: true,
        touched: {}
      };
      return;
    }
    // rule 2c: leave existing draftEntry alone
  }

  updateDraftEntry(changes: Partial<{ text: string; attachments: { filename: string }[]; users: string[] }>): void {
    if (!this.draftEntry) return;
    if (Object.prototype.hasOwnProperty.call(changes, 'text') && typeof changes.text === 'string') {
      this.draftEntry.text = changes.text;
      this.draftEntry.touched.text = true;
    }
    if (changes.attachments) {
      this.draftEntry.attachments = changes.attachments.slice();
      this.draftEntry.touched.attachments = true;
    }
    if (changes.users) {
      this.draftEntry.users = changes.users.slice();
      this.draftEntry.touched.users = true;
    }
  }

  clearDraftEntryTouchFlagsAfterPersist() {
    if (!this.draftEntry) return;
    this.draftEntry.touched = {};
  }

  /** Discard the working draft conversation entry (unsaved or persisted). Does not perform server delete. */
  discardDraftEntry() {
    if (!this.draftEntry) return;
    this.draftEntry = null;
    this.baselineDraftEntry = null;
  }

  applyLoaded(entries: ViewConversationEntry[], users: string[]) {
    this.entries = entries;
    this.users = users;
    this.loaded = true;
  }

  /** Update ticket model & version (without emitting conversation events unless needed) */
  applyTicket(ticket: BranchQuoteSupport, masterDocumentFilename?: string) {
    this.ticket = ticket;
    if (this.ticket.id !== this.id) {
      throw new Error('Ticket ID mismatch in session applyTicket');
    }
    // Adopt support type from ticket if numeric enum
    if (typeof ticket.type === 'number') {
      this.supportType = ticket.type as BranchQuoteSupportItemType;
    }
    this.masterDocumentConversationId = ticket.masterDocumentConversationId ?? null;
    this.recordVersion = ticket.recordVersion ?? null;
    // Refresh baseline if none OR if we have no draft (safe to update) – leave baseline if draft exists (used for conflict detection)
    if (!this.draftMeta) {
      this.baselineMeta = {
        subject: ticket.subject ?? null,
        quoteItemLinks: { ...(ticket.quoteItemLink || {}) },
        // master document id (if present) – assume ticket.masterDocumentConversationId field name or nested; adapt if needed
        masterDocumentFilename: masterDocumentFilename ?? null,
        version: ticket.recordVersion ?? null
      };
    } else {
      // Conflict detection: if server mutated a field different from baseline while user touched that field, mark conflict.
      const base = this.baselineMeta;
      if (base) {
        const draft = this.draftMeta;
        const serverSubject = ticket.subject ?? null;
        if (draft.touched.subject && base.subject !== serverSubject && draft.subject !== serverSubject) {
          draft.conflict.subject = true;
        }
        // Quote item links comparison (shallow key set + values by id)
        const serverLinks: Record<string, SupportQuoteItemLinkDetail> = ticket.quoteItemLink || {};
        if (draft.touched.links && base.quoteItemLinks) {
          const changed =
            this._linksDiffer(base.quoteItemLinks, serverLinks) && this._linksDiffer(draft.quoteItemLinks, serverLinks);
          if (changed) draft.conflict.links = true;
        }
        // Master doc id
        const serverMaster = masterDocumentFilename ?? null;
        if (
          draft.touched.masterDoc &&
          base.masterDocumentFilename !== serverMaster &&
          draft.masterDocumentFilename !== serverMaster
        ) {
          draft.conflict.masterDoc = true;
        }
      }
    }
  }

  /** Update or delete an entry in-place (assumes entry exists in cache). */
  applyEntryMutation(updated: ViewConversationEntry | null, entryId: string) {
    if (!this.loaded) return; // ignore if not loaded
    if (updated === null) {
      this.entries = this.entries.filter(e => e.id !== entryId);
    } else {
      const idx = this.entries.findIndex(e => e.id === entryId);
      if (idx >= 0) Object.assign(this.entries[idx], updated);
    }
  }

  /** Compare two quote item link records for differences */
  _linksDiffer(a: Record<string, SupportQuoteItemLinkDetail>, b: Record<string, SupportQuoteItemLinkDetail>): boolean {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});
    if (aKeys.length !== bKeys.length) return true;
    for (const k of aKeys) {
      const av = a[k];
      const bv = b[k];
      if (!bv) return true;
      if (av.isLinked !== bv.isLinked) return true;
      if (av.textDisplay !== bv.textDisplay) return true;
    }
    return false;
  }
}

// ---- Draft Metadata Helpers (kept here for cohesion; service uses them) ----
export function beginMetadataEdit(session: SupportTicketSession) {
  if (session.draftMeta || !session.baselineMeta) return;
  session.draftMeta = {
    subject: session.baselineMeta.subject,
    quoteItemLinks: { ...session.baselineMeta.quoteItemLinks },
    masterDocumentFilename: session.baselineMeta.masterDocumentFilename,
    touched: {},
    conflict: {}
  };
}

export function updateDraftMetadata(
  session: SupportTicketSession,
  changes: Partial<{
    subject: string | null;
    quoteItemLinks: Record<string, SupportQuoteItemLinkDetail>;
    masterDocumentFilename: string | null;
  }>
) {
  if (!session.baselineMeta) return;
  if (!session.draftMeta) beginMetadataEdit(session);
  const draft = session.draftMeta!;
  if (Object.prototype.hasOwnProperty.call(changes, 'subject')) {
    draft.subject = changes.subject ?? null;
    draft.touched.subject = true;
    // Resolving conflict automatically if server now matches draft
    if (draft.conflict.subject) draft.conflict.subject = false;
  }
  if (Object.prototype.hasOwnProperty.call(changes, 'quoteItemLinks') && changes.quoteItemLinks) {
    draft.quoteItemLinks = { ...changes.quoteItemLinks };
    draft.touched.links = true;
    if (draft.conflict.links) draft.conflict.links = false;
  }
  if (Object.prototype.hasOwnProperty.call(changes, 'masterDocumentFilename')) {
    draft.masterDocumentFilename = changes.masterDocumentFilename ?? null;
    draft.touched.masterDoc = true;
    if (draft.conflict.masterDoc) draft.conflict.masterDoc = false;
  }
}

export function discardDraftMetadata(session: SupportTicketSession) {
  session.draftMeta = null;
}

export function hasMetadataChanges(session: SupportTicketSession): boolean {
  if (!session.draftMeta || !session.baselineMeta) return false;
  const d = session.draftMeta;
  const b = session.baselineMeta;
  return !!(
    (d.touched.subject && d.subject !== b.subject) ||
    (d.touched.links && session._linksDiffer && session._linksDiffer(d.quoteItemLinks, b.quoteItemLinks)) ||
    (d.touched.masterDoc && d.masterDocumentFilename !== b.masterDocumentFilename)
  );
}

export function validateDraftMetadata(session: SupportTicketSession): string[] {
  if (!session.draftMeta) return [];
  const errs: string[] = [];
  const subj = (session.draftMeta.subject ?? '').trim();
  if (!subj) {
    // Use same localized string as new ticket subject validation
    try {
      const type: BranchQuoteSupportItemType | undefined = session.ticket?.type ?? BranchQuoteSupportItemType.All;
      if (typeof type === 'number') {
        errs.push(
          LangBranchQuoteSupportNew.pleaseAddSupportSubject({
            supportType: getSupportTypeDisplayValue(type)
          })
        );
      } else {
        errs.push(LangBranchQuoteSupportNew.pleaseAddSupportSubject({ supportType: '' }));
      }
    } catch {
      errs.push('Subject is required');
    }
  }
  if (subj.length > 200) errs.push('Subject exceeds 200 characters');
  return errs;
}

export function gatherPendingMetadataUpdate(session: SupportTicketSession): {
  subject?: string | null;
  quoteItemLink?: Record<string, SupportQuoteItemLinkDetail> | null;
  masterDocumentFilename?: string | null;
  hasConflicts: boolean;
} | null {
  if (!session.draftMeta || !session.baselineMeta) return null;
  const d = session.draftMeta;
  const b = session.baselineMeta;
  const out: any = { hasConflicts: !!(d.conflict.subject || d.conflict.links || d.conflict.masterDoc) };
  if (d.touched.subject && d.subject !== b.subject) out.subject = d.subject;
  if (d.touched.links && session._linksDiffer && session._linksDiffer(d.quoteItemLinks, b.quoteItemLinks))
    out.quoteItemLink = d.quoteItemLinks;
  if (d.touched.masterDoc && d.masterDocumentFilename !== b.masterDocumentFilename)
    out.masterDocumentFilename = d.masterDocumentFilename;
  const meaningful = Object.keys(out).some(k => k !== 'hasConflicts');
  return meaningful ? out : null;
}

export function ensureSession(
  map: Map<string, SupportTicketSession>,
  id: string,
  kind: 'draft' | 'editing' = 'editing',
  supportType?: BranchQuoteSupportItemType
): SupportTicketSession {
  let session = map.get(id);
  if (!session) {
    session = new SupportTicketSession(id, kind, supportType);
    map.set(id, session);
  } else {
    // Upgrade existing session kind/supportType if more specific info provided
    if (kind === 'draft' && session.kind !== 'draft') session.kind = 'draft';
    if (typeof supportType === 'number' && session.supportType !== supportType) session.supportType = supportType;
  }
  return session;
}

// ---- Draft Conversation Entry Change Detection ----
/**
 * Determine whether the currently wrapped persisted draft conversation entry has unsaved changes
 * compared to the baseline snapshot captured when it was wrapped.
 * Only evaluates true for persisted drafts; unsaved new drafts are handled by intrinsic empty/content checks elsewhere.
 */
export function hasPersistedDraftEntryChanges(session: SupportTicketSession): boolean {
  const draft = session.draftEntry;
  if (!draft || !draft.existingPersisted) return false; // only compare persisted drafts
  const base = session.baselineDraftEntry;
  if (!base || base.id !== draft.id) return true; // safety: if baseline missing, treat as changed so save allowed
  const norm = (v: string | undefined | null) => (v || '').trim();
  if (norm(draft.text) !== norm(base.text)) return true;
  // Compare attachment filename sets (order-insensitive)
  const aNow = new Set((draft.attachments || []).map(a => a.filename));
  const aBase = new Set(base.attachments || []);
  if (aNow.size !== aBase.size) return true;
  for (const f of aNow) if (!aBase.has(f)) return true;
  // Compare user id sets (order-insensitive)
  const uNow = new Set(draft.users || []);
  const uBase = new Set(base.users || []);
  if (uNow.size !== uBase.size) return true;
  for (const u of uNow) if (!uBase.has(u)) return true;
  return false;
}
